import { useState, useCallback } from 'react';
import { api } from '../service/api.service';
import { extractError } from '../service/response.service';
import t from '../service/translate.service';

/**
 * Voucher types
 */
export const VOUCHER_TYPES = {
  MARKETING: 'marketing',
  CAPEX: 'capex'
};

/**
 * Hook for requesting vouchers (marketing or CAPEX)
 * Sends email to the responsible sales/marketing team
 * 
 * @returns {Object} - { requestVoucher, loading, error, success, resetState }
 */
const useVoucherRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  /**
   * Reset the hook state
   */
  const resetState = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
    setSuccessMessage('');
  }, []);

  /**
   * Request a voucher (marketing or CAPEX)
   * 
   * @param {Object} params - Request parameters
   * @param {string} params.contractNumber - The contract number (MV number)
   * @param {string} params.voucherType - Type of voucher ('marketing' or 'capex')
   * @param {string} params.tenantName - Name of the tenant
   * @param {string} params.address - Address of the property
   * @returns {Promise<Object>} - { success: boolean, message?: string }
   */
  const requestVoucher = useCallback(async ({ contractNumber, voucherType, tenantName, address }) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    setSuccessMessage('');

    try {
      // Validate inputs
      if (!contractNumber) {
        console.error('useVoucherRequest: contractNumber is missing', { contractNumber, voucherType });
        throw new Error('VOUCHER_REQUEST_NO_CONTRACT');
      }
      if (!voucherType || !Object.values(VOUCHER_TYPES).includes(voucherType)) {
        console.error('useVoucherRequest: invalid voucherType', { contractNumber, voucherType });
        throw new Error('VOUCHER_REQUEST_INVALID_TYPE');
      }

      console.log('useVoucherRequest: sending request', { contractNumber, voucherType, tenantName, address });

      const response = await api.post(`/vouchers/${contractNumber}/request`, {
        voucherType,
        tenantName,
        address
      });

      console.log('useVoucherRequest: response received', response.data);

      const voucherName = voucherType === VOUCHER_TYPES.MARKETING
        ? (t['VOUCHER_MARKETING_TITLE'] || 'Marketing voucher')
        : (t['VOUCHER_CAPEX_TITLE'] || 'CAPEX voucher');

      const message = t['VOUCHER_REQUEST_SUCCESS_MESSAGE']
        ? t['VOUCHER_REQUEST_SUCCESS_MESSAGE'].replace('{voucherName}', voucherName)
        : `Your ${voucherName} request has been submitted successfully. The sales team will process your request shortly.`;

      setSuccess(true);
      setSuccessMessage(message);
      setLoading(false);

      return {
        success: true,
        message,
        data: response.data
      };
    } catch (err) {
      console.error('useVoucherRequest: error', err);
      
      // Handle specific validation errors
      let errorMessage;
      if (err.message === 'VOUCHER_REQUEST_NO_CONTRACT') {
        errorMessage = t['VOUCHER_REQUEST_NO_CONTRACT'] || 'Please select a contract first.';
      } else if (err.message === 'VOUCHER_REQUEST_INVALID_TYPE') {
        errorMessage = t['VOUCHER_REQUEST_INVALID_TYPE'] || 'Invalid voucher type.';
      } else {
        // Handle API errors
        const apiError = extractError(err);
        if (apiError === 'VOUCHER_ALREADY_REQUESTED') {
          errorMessage = t['VOUCHER_ALREADY_REQUESTED'] || 'You have already requested this voucher. Your request is being processed.';
        } else {
          errorMessage = t[apiError] || t['VOUCHER_REQUEST_ERROR'] || 'Failed to submit voucher request. Please try again.';
        }
      }
      
      setError(errorMessage);
      setLoading(false);

      return {
        success: false,
        message: errorMessage
      };
    }
  }, []);

  /**
   * Request a marketing voucher
   */
  const requestMarketingVoucher = useCallback(async ({ contractNumber, tenantName, address }) => {
    return requestVoucher({
      contractNumber,
      voucherType: VOUCHER_TYPES.MARKETING,
      tenantName,
      address
    });
  }, [requestVoucher]);

  /**
   * Request a CAPEX voucher
   */
  const requestCapexVoucher = useCallback(async ({ contractNumber, tenantName, address }) => {
    return requestVoucher({
      contractNumber,
      voucherType: VOUCHER_TYPES.CAPEX,
      tenantName,
      address
    });
  }, [requestVoucher]);

  return {
    requestVoucher,
    requestMarketingVoucher,
    requestCapexVoucher,
    loading,
    error,
    success,
    successMessage,
    resetState
  };
};

export default useVoucherRequest;
